import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { DocNode as ADF } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	ROVO_SKILL_COMBINED_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import {
	isStoreHuman,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useDraftsController } from '@atlassian/conversation-assistant-store/controllers/drafts';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';

import { ErrorBoundary as AIMateErrorBoundary } from '../../common/ui/error-boundary';

const getSkillSlug = (): 'build-agent' | 'build-rovo-agent' =>
	fg('sltns-1160-rename-to-build-agent') ? 'build-agent' : 'build-rovo-agent';

const getSkillPrompt = (): string => `/${getSkillSlug()}`;

// clearPlaceholder() only clears pending placeholder state, not content already
// applied to the editor — clearing the input requires replacing it with this instead.
const EMPTY_CHAT_INPUT_ADF: ADF = {
	type: 'doc',
	version: 1,
	content: [{ type: 'paragraph' }],
};

const adfHasSkill = (adfValue: ADF): boolean => {
	let hasSkill = false;
	const skillSlug = getSkillSlug();
	traverse(adfValue, {
		text: (node) => {
			if (hasSkill) return false;
			if (textHasSkill(node.text)) {
				hasSkill = true;
				return false;
			}
		},

		inlineExtension: (node) => {
			if (hasSkill) return false;
			if (
				node.attrs?.extensionKey === ROVO_SKILL_COMBINED_EXTENSION_KEY &&
				node.attrs?.extensionType === ROVO_SKILL_EXTENSION_TYPE &&
				node.attrs?.parameters?.slug === skillSlug
			) {
				hasSkill = true;
				return false;
			}
		},
	});

	return hasSkill;
};

const textHasSkill = (text: string | undefined): boolean => {
	if (!text) return false;
	const skillPrompt = getSkillPrompt();

	const index = text.indexOf(skillPrompt);
	if (index === -1) return false;

	return text[index + skillPrompt.length] === ' ' || index + skillPrompt.length === text.length;
};

function getMessageHasSkill(message: StoreChatItem) {
	if (!isStoreHuman(message)) return false;

	const { content } = message.content;

	if (typeof content === 'string') {
		return textHasSkill(content);
	} else {
		return adfHasSkill(content);
	}
}

const isDraftMessageEmpty = (draftMessage: ADF | null | undefined): boolean => {
	if (draftMessage === null || draftMessage === undefined) {
		return true;
	}
	return !draftMessage.content || draftMessage.content.length === 0;
};

type StickySkillListenerProps = {
	conversationId: string;
};

const StickySkillListenerInner = ({
	conversationId,
}: StickySkillListenerProps): React.JSX.Element => {
	const skillPrompt = getSkillPrompt();
	const [messages] = useChatMessages({ conversationId });
	const firstMessage = messages.at(0);
	const { draftMessage } = useDraftsController(conversationId);
	const { insertPlaceholder, insertPlaceholderWithAdf } = useEditorPlaceholder();
	const [lastInsertMessageCount, setLastInsertMessageCount] = useState(-1);

	const messageCount = useMemo(() => {
		return messages.filter((m) => m.type === 'HUMAN').length;
	}, [messages]);

	const messageHasSkill = useMemo(() => {
		if (!firstMessage) return false;
		return getMessageHasSkill(firstMessage);
	}, [firstMessage]);

	useEffect(() => {
		if (!messageHasSkill) return;
		if (lastInsertMessageCount === messageCount) return;
		if (!isDraftMessageEmpty(draftMessage)) return;

		setLastInsertMessageCount(messageCount);
		insertPlaceholder(`${skillPrompt} `, 'skill');
	}, [
		draftMessage,
		messageHasSkill,
		lastInsertMessageCount,
		messageCount,
		insertPlaceholder,
		skillPrompt,
	]);

	// Read by the unmount cleanup below, which must only run once (on unmount).
	const latestRef = useRef({ messageHasSkill, draftMessage });
	latestRef.current = { messageHasSkill, draftMessage };

	useEffect(() => {
		if (!fg('rovo_chat_clear_sticky_skill_on_unmount')) return;

		// On unmount (leaving this conversation), clear a still-untouched sticky
		// skill prompt so it doesn't carry over into the next conversation.
		return () => {
			const { messageHasSkill, draftMessage } = latestRef.current;
			if (!messageHasSkill || !draftMessage) return;
			if (!adfHasSkill(draftMessage)) return;

			insertPlaceholderWithAdf('', EMPTY_CHAT_INPUT_ADF);
		};
	}, [insertPlaceholderWithAdf]);

	return <ExperienceSuccess name={ExperienceName.LOAD_STICKY_SKILL_LISTENER} />;
};

const EmptyFallback = () => null;

export const StickySkillListener = ({
	conversationId,
}: StickySkillListenerProps): React.JSX.Element => {
	return (
		<AIMateErrorBoundary
			fallback={<EmptyFallback />}
			experienceName={ExperienceName.LOAD_STICKY_SKILL_LISTENER}
		>
			<ExperienceStart name={ExperienceName.LOAD_STICKY_SKILL_LISTENER} />
			<StickySkillListenerInner conversationId={conversationId} />
		</AIMateErrorBoundary>
	);
};
